// Week math for the Timeline "Weekly" timeframe.
//
// Weeks are Monday-anchored ISO weeks that span a full 7 days (Mon–Sun, weekends included).
// Only the *label* shows the Mon–Fri work range (e.g. "Mar 2–6", or "Mar 30–Apr 3" across months).
// Pills are positioned day-proportionally within their start/end week, so a Wed→Fri campaign
// renders as a partial-week bar and weekend days occupy their proportional slice.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Parse the YYYY-MM-DD portion of a stored ISO date as a LOCAL calendar day (avoids the
 *  UTC-midnight → previous-day shift west of UTC — stored schedule dates are UTC midnight). */
export function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** Local midnight copy (drops any time component). */
function atMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Monday of the week containing `d` (ISO: Sunday belongs to the previous Monday's week). */
export function mondayOf(d: Date): Date {
  const day = d.getDay(); // 0 Sun … 6 Sat
  const offset = (day + 6) % 7; // Mon → 0, Sun → 6
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset);
}

/** Whole-day difference a − b (both floored to local midnight). */
function dayDiff(a: Date, b: Date): number {
  return Math.round((atMidnight(a).getTime() - atMidnight(b).getTime()) / DAY_MS);
}

/** Work-week label, e.g. "Mar 2–6" (same month) or "Mar 30–Apr 3" (crosses a month). */
export function weekLabel(monday: Date): string {
  const fri = addDays(monday, 4);
  const m1 = MONTHS[monday.getMonth()];
  const m2 = MONTHS[fri.getMonth()];
  return monday.getMonth() === fri.getMonth()
    ? `${m1} ${monday.getDate()}–${fri.getDate()}`
    : `${m1} ${monday.getDate()}–${m2} ${fri.getDate()}`;
}

export interface WeekColumn {
  key: string; // ISO date of the Monday (unique)
  monday: Date;
  sunday: Date; // Monday + 6 (inclusive week end, includes the weekend)
  label: string; // work-week label
}

/** All Monday-anchored week columns covering [rangeStart, rangeEnd] inclusive. */
export function buildWeekColumns(rangeStart: Date, rangeEnd: Date): WeekColumn[] {
  const cols: WeekColumn[] = [];
  let cur = mondayOf(rangeStart);
  const end = mondayOf(rangeEnd);
  // Guard against pathological ranges producing an unbounded loop.
  let safety = 0;
  while (cur.getTime() <= end.getTime() && safety < 520) {
    const sunday = addDays(cur, 6);
    cols.push({
      key: `${cur.getFullYear()}-${cur.getMonth() + 1}-${cur.getDate()}`,
      monday: cur,
      sunday,
      label: weekLabel(cur),
    });
    cur = addDays(cur, 7);
    safety++;
  }
  return cols;
}

/**
 * Day-proportional left offset and width (in px) for a date range across week columns.
 * `firstMonday` is the Monday of the first week column. End is inclusive.
 * Returns null when the range is entirely outside the built week grid.
 */
export function weekBarMetrics(
  start: Date,
  end: Date,
  firstMonday: Date,
  weekCount: number,
  weekWidthPx: number,
): { left: number; width: number } | null {
  const s = atMidnight(start);
  const e = atMidnight(end < start ? start : end);

  // Fractional week position from the grid's first Monday. Each day = 1/7 of a column.
  const startDays = dayDiff(s, firstMonday); // days from first Monday to start
  const endDaysExclusive = dayDiff(e, firstMonday) + 1; // +1 → inclusive end day fully covered

  const gridDays = weekCount * 7;
  if (endDaysExclusive <= 0 || startDays >= gridDays) return null; // outside the grid

  const clampedStart = Math.max(0, startDays);
  const clampedEnd = Math.min(gridDays, endDaysExclusive);

  const left = (clampedStart / 7) * weekWidthPx;
  const width = Math.max((weekWidthPx / 7) * 0.6, ((clampedEnd - clampedStart) / 7) * weekWidthPx);
  return { left, width };
}
